import { Input } from '@amzn/awsui-components-react';
import React from 'react';
import CONSTANTS from 'src/utils/constants';
import { AllocationGroupFormula, FormulaMapping } from 'src/models/tp-allocation/AllocationGroup';
import ATPTextAreaAutoComplete, { AutoSuggesstListItem } from 'src/components/shared/ATPTextAreaAutoComplete';
import ATPCompaniesAutosuggest from 'src/components/shared/ATPCompaniesAutosuggest';

const FIELDS = CONSTANTS.WORKSHEET_ALLOCATION_FORMULA_FIELDS;

/** A Step 3 grid row: a formula plus the display values the grid adds. */
export type DasFormulaRow = AllocationGroupFormula & {
    mappingFormatted: string;
    formulaOutputFormatted: string;
    errorIndicator?: React.ReactNode;
};

export interface DasFormulaEditorsData {
    suggestionsList: AutoSuggesstListItem[];
    companiesList: string[];
    onTextAreaCaretPositionChanged: (position: number) => void;
    shouldShowValues?: boolean;
}

export interface DasFormulaCellProps {
    value?: string | FormulaMapping;
    row: { index: number; original: DasFormulaRow };
    column: { id: string };
    onRowUpdated: (rowIndex: number, columnId: string, value?: string | FormulaMapping) => void;
    editorsData: DasFormulaEditorsData;
}

/** Company codes typed in the mapping editor: trimmed, blanks and repeats dropped. */
export const toCompanyMapping = (text: string): FormulaMapping => ({
    mappingType: CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI,
    values: [...new Set(text.split(',').map((v) => v.trim()).filter((v) => v !== ''))],
});

/**
 * Cell renderer of the Step 3 formulas grid: the formula editor, company picker and name input for the row
 * being edited, plain text otherwise. Adapted from the TPA allocation group formulas grid; keep the two in sync.
 */
export const DasFormulaEditableCell = ({
    value: initialValue,
    row: { index, original },
    column: { id },
    onRowUpdated,
    editorsData,
}: DasFormulaCellProps) => {
    const [value, setValue] = React.useState(initialValue as string | undefined);

    React.useEffect(() => {
        setValue(initialValue as string | undefined);
    }, [initialValue]);

    const onBlur = () => onRowUpdated(index, id, value);
    const formula = original;
    const status = `save-status-${formula.savingStatus}`;
    const key = `${id}_${index}`;

    if (formula.isBeingEdited) {
        switch (id) {
            case FIELDS.OVERVIEW:
                return <ATPTextAreaAutoComplete key={key} editorsData={editorsData} initialValue={(initialValue as string) ?? ''}
                                                onTextAreaBlur={(e: { textAreaValue: string }) => onRowUpdated(index, id, e.textAreaValue)} />;
            case FIELDS.MAPPING:
                return <ATPCompaniesAutosuggest key={key} className="coaAutosuggest" companies={editorsData.companiesList}
                                                selectedCompanies={(initialValue as FormulaMapping)?.values || []}
                                                initialValue={original.mappingFormatted} isEditing
                                                onBlur={(text: string) => onRowUpdated(index, id, toCompanyMapping(text))} />;
            case FIELDS.DESCRIPTION:
                return <div key={key} className={`${status} cell-text ${id}Cell`}>
                    <Input className={`dataSourcesButton ${id}Editor${original.allocationFormulaId}`} placeholder="Enter value"
                           value={value ?? ''} onChange={({ detail }) => setValue(detail.value)} onBlur={onBlur} />
                    {original.errorIndicator && <span className="inlineErrorContainer">{original.errorIndicator}</span>}
                </div>;
        }
    } else {
        switch (id) {
            case FIELDS.DESCRIPTION:
                return <div key={key} className={`${status} cell-text formula_${id}Cell`}>
                    <span>{value}</span>
                    {original.errorIndicator && <div className="inlineErrorContainer">{original.errorIndicator}</div>}
                </div>;
            case FIELDS.OVERVIEW:
                return <div key={key} className={status}>
                    {formula.expression ? formula.expressionFormatted : 'Click to enter the allocation formula'}
                    {editorsData.shouldShowValues && formula.outputBreakdown ? <div className="greyBadge">{formula.outputBreakdown}</div> : null}
                </div>;
            case FIELDS.MAPPING:
                return <div key={key} className={`${status} cell-text ${id}Cell`}>{original.mappingFormatted}</div>;
        }
    }
    return <div key={key} className={`${status} cell-text ${id}Cell`}>{value as string}</div>;
};

export const dasFormulaCellEditor = {
    Cell: DasFormulaEditableCell,
};

export const DAS_FORMULA_MENU_ACTIONS = {
    EDIT: { text: 'Edit', id: 'Edit' },
    DUPLICATE: { text: 'Duplicate', id: 'Duplicate' },
    REMOVE: { text: 'Remove', id: 'Remove' },
};

export const DAS_FORMULA_MENU_ITEMS = [
    DAS_FORMULA_MENU_ACTIONS.EDIT,
    DAS_FORMULA_MENU_ACTIONS.DUPLICATE,
    DAS_FORMULA_MENU_ACTIONS.REMOVE,
];
